/**
 * Cliente do serviço Vigia de esgotados (services/vigia-esgotados, 127.0.0.1:8111). Same-origin: sessão S2 via
 * nginx auth_request; escritas levam X-Token injetado só no servidor (o browser nunca vê token).
 */
export class VigiaApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'VigiaApiError'
    this.status = status
  }
}

const BASE = '/api/vigia-esgotados'

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(BASE + path, { credentials: 'include', cache: 'no-store', ...init })
  } catch {
    throw new VigiaApiError(0, 'Serviço do Vigia de esgotados inacessível (fiesta-vigia-esgotados, porta 8111).')
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as { detail?: unknown } | null
    const detail = typeof corpo?.detail === 'string' ? corpo.detail : undefined
    throw new VigiaApiError(resp.status, detail || `Vigia de esgotados respondeu HTTP ${resp.status}.`)
  }
  return resp.json() as Promise<T>
}

const post = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })

export type Acao = 'pausar_anuncio' | 'zerar_variacao'

export type Canal = { ok: boolean; erro: string | null; anuncios: number; vendaveis: number; lido_em: string }

export type StatusVigia = {
  ultimo_ciclo?: string
  duracao_s?: number
  erro?: string | null
  canais?: Record<string, Canal>
  vigiados?: number
  alertas_abertos?: number
  escrita: boolean
  rodando_desde: string | null
  ciclo_min: number
  ciente_horas: number
}

export type SkuVigiado = {
  id: string
  sku: string
  motivo: string
  criado_em: string
  criado_por: string
  kits: string[] | null
  anuncios: number
  vendaveis: number
}

export type AnuncioVigiado = {
  id: string
  chave: string
  marketplace: 'mercadolivre' | 'shopee' | 'shein' | 'tiktok' | 'amazon'
  loja: string
  item_id: string
  model_id: string | null
  variacao: string | null
  sku_anuncio: string
  skus_vigiados: string[]
  e_kit: boolean
  titulo: string
  link: string | null
  status: string
  estoque: number | null
  reservado_promo: number | null
  full: boolean | null
  vendavel: boolean
  motivo: string | null
  trava_campanha?: boolean
  lido_em: string
  acoes: Acao[]
}

export type RegistroAcao = { tipo: Acao | 'ciente'; por: string; em: string; ok: boolean; mensagem: string }

export type AlertaVigia = Omit<AnuncioVigiado, 'acoes' | 'vendavel' | 'lido_em'> & {
  aberto_em: string
  visto_em: string
  fechado_em: string | null
  fechado_por: 'leitura' | 'acao' | null
  ciente_ate: string | null
  acoes: RegistroAcao[]
  acoes_possiveis: Acao[]
}

export const fetchStatus = () => api<StatusVigia>('/status')
export const fetchSkus = () => api<{ itens: SkuVigiado[] }>('/skus').then((r) => r.itens)
export const fetchAnuncios = () => api<{ itens: AnuncioVigiado[] }>('/anuncios').then((r) => r.itens)
export const fetchAlertas = (q: 'abertos' | 'popup' | 'todos') =>
  api<{ itens: AlertaVigia[] }>(`/alertas${q === 'todos' ? '' : `?${q === 'popup' ? 'popup' : 'abertos'}=1`}`).then((r) => r.itens)

export const incluirSkus = (skus: string, motivo: string) =>
  api<{ incluidos: string[]; fora_do_tiny: string[]; mensagem: string }>('/skus', post({ skus, motivo }))
export const removerSku = (sku: string) => api<{ removidos: number }>(`/skus/${encodeURIComponent(sku)}`, { method: 'DELETE' })
export const reler = () => api<{ ok: boolean; mensagem: string }>('/reler', { method: 'POST' })
export const ciente = (id: string) => api<{ ok: boolean; ciente_ate: string }>(`/alertas/${encodeURIComponent(id)}/ciente`, { method: 'POST' })
export const pausar = (id: string, acao: Acao) =>
  api<{ ok: boolean; mensagem: string }>(`/alertas/${encodeURIComponent(id)}/pausar`, post({ acao }))
